import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from "react";
import { RCCAlert, RCCDropDownItem, RCCHookToast, RCCIcon, RCCServicesApi } from "react-common-components";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import Loader from "src/components/Loader";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { IRefModalGoalManagement } from "src/pages/Indicators/Panel/components/ModalGoalManagement/interface";
import ModalGoalManagement from "src/pages/Indicators/Panel/components/ModalGoalManagement";
import { IRefIndicatorModal } from "src/components/ModalIndicator/interface";
import IndicatorModal from "src/components/ModalIndicator";
import formatLink from "src/utils/formatLink";
import { PermissionEnum } from "src/context/Auth/interface";
import CheckPermission from "src/components/CheckPermission";
import { useAuth } from "src/context/Auth/context";
import formatDate from "src/utils/formatDate";
import { IIndicatorsLevel, IListIndicators, IListOfIndicators } from "./interface";
import executeQueryListOfIndicators from "./executeQueryListOfIndicators";
import GroupIndicators from "./GroupIndicators";
import { IndicatorsGroupers } from "../interface";
import executeQueryListOfIndicatorsChildren from "./executeQueryListOfIndicatorsChildren";
import { IRefActionsModalChartIndicators } from "./ModalIndicatorChart/interface";
import { IRefDeleteIndicatorModal } from "../../Indicators/interface";
import DeleteModalIndicator from "../../Indicators/deleteModalIndicator";
import ModalIndicatorChart from "./ModalIndicatorChart";
import ModalIndicatorDetails from "./ModalIndicatorDetails";
import ModalIndicatorValuesOfPeriod from "./ModalValuesOfPeriod";
import { IRefActionsModalDetailsIndicators } from "./ModalIndicatorDetails/interface";
import { IRefActionsModalValuesOfPeriodIndicators } from "./ModalValuesOfPeriod/interface";
import { useIndicatorsPanel } from "../indicatorsPanelContext";

export interface IRefListOfIndicators{
	executeQuery: () => Promise<void>;
}

const ListOfIndicators: React.ForwardRefRenderFunction<IRefListOfIndicators, IListOfIndicators> = ({
	onCallbackDelete,
	onCallbackUpdate,
	filters,
	groupersSelect,
	groupers,
	showValues,
	filterList,
	layoutList,
	onCallbackChangeValuesIndicator,
	defaultChecked
}, ref) => {
	const { administrator, superUser } = useAuth();
	const { translate } = useTranslation();
	const { showCheckboxes, addSelectedCheckboxId, actionHideCheckboxes, addAllSelectedCheckboxesId } = useIndicatorsPanel();
	const { addToast } = RCCHookToast();
	const { setLoading: setLoadingBackdrop } = useLoaderBackdrop();
	const { confirm } = useConfirmModal();

	const refModalDelete = useRef<IRefDeleteIndicatorModal>(null);
	const modalChartIndicatorsValues = useRef<IRefActionsModalChartIndicators>(null);
	const modalGoalManagement = useRef<IRefModalGoalManagement>(null);
	const refmodalindicators = useRef<IRefIndicatorModal>(null);
	
	const modalDetailsIndicators = useRef<IRefActionsModalDetailsIndicators>(null);
	const modalValuesOfPeriodIndicators = useRef<IRefActionsModalValuesOfPeriodIndicators>(null);

	const [loading, setLoading] = useState(true);
	const [list, setList] = useState<IListIndicators[]>([]);

	const submitMarkToUpdate = (id: number | Array<number>, description: string, baseYear: string, periodId?: number) => {
		confirm(
			translate("Marcar para atualizar"), 
			`${translate("Deseja realmente marcar para atualizar os valores do indicador")} "${description}" ?`,
			async () => {
				await RCCServicesApi.postRequest({
					method: "Indicators/MarkToUpdate",
					body: {
						indicators: Array.isArray(id) ? id : [id],
						baseYear,
						periodId: periodId || null
					}
				});

				addToast({
					color: "green",
					description: `${translate("Valores do indicador marcados para atualizar com sucesso")}. ${translate("Será feito na próxima execução da integração")}`,
					title: translate("Sucesso")
				});
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao atualizar valores do indicador"), addToast);
			},
			false
		);
	};

	const options = (row: IListIndicators) => <OptionsColumn>
		{(administrator || superUser || row.permission >= 5) && (
			<CheckPermission minimumPermissionRequired={PermissionEnum.VIEW}>
				<RCCDropDownItem 
					selfEnclose
					onClick={() => {
						if(showCheckboxes)
							actionHideCheckboxes();

						refmodalindicators.current?.actionButtonChange(row.iqc001Id,false,() => onCallbackUpdateFunction(row.iqc001Id,row.iqc001Nivel));
					}}
				>
					<RCCIcon name="pencil" />
					{translate("Alterar")}
				</RCCDropDownItem>
			</CheckPermission>
		)} 
		<RCCDropDownItem 
			selfEnclose
			onClick={() => {
				if(showCheckboxes)
					actionHideCheckboxes();

				modalChartIndicatorsValues.current?.actionButtonOpenChartIndicators(row.iqc001Id, row.iqc001Descricao, groupersSelect.baseYear, row.accumulatedValues !== null && row.accumulatedValues.length > 0, filters?.startDate, filters?.endDate);
			}}
		>
			<RCCIcon name="chartBar" />
			{translate("Visualizar gráfico")}
		</RCCDropDownItem>
		<RCCDropDownItem 
			selfEnclose
			onClick={() => {
				if(showCheckboxes)
					actionHideCheckboxes();

				modalDetailsIndicators.current?.actionButtonOpenDetailsIndicators(row.iqc001Id, row.iqc001Descricao);
			}}
		>
			<RCCIcon name="info" />
			{translate("Detalhes")}
		</RCCDropDownItem>
		{row.iqc001Linkexterno !== null && row.iqc001Linkexterno.length > 0 && (
			<RCCDropDownItem 
				selfEnclose
				onClick={() => {
					window.open(formatLink(row.iqc001Linkexterno), "_blank");
				}}
			>
				<RCCIcon name="linkExternal" />
				Link
			</RCCDropDownItem>
		)}
		{(administrator || superUser || row.permission >= 3) && (
			<CheckPermission minimumPermissionRequired={PermissionEnum.VIEW}>
				<RCCDropDownItem 
					selfEnclose
					onClick={() => {
						if(showCheckboxes)
							actionHideCheckboxes();

						submitMarkToUpdate(row.iqc001Id, row.iqc001Descricao, groupersSelect.baseYear);
					}}
				>
					<RCCIcon name="settings" />
					{translate("Marcar para atualizar")}
				</RCCDropDownItem>
				{(administrator || superUser || row.permission >= 4) && (
					<RCCDropDownItem 
						selfEnclose
						onClick={() => {
							if(showCheckboxes)
								actionHideCheckboxes();

							modalGoalManagement.current?.actionOpen(row.iqc001Id,groupersSelect.baseYear, `${row.iqc001Descricao} - ${groupersSelect.baseYear}`,() => onCallbackUpdateFunction(row.iqc001Id));
						}}
					>
						<RCCIcon name="dollar" />
						{translate("Gerenciar metas")}
					</RCCDropDownItem>
				)}
			</CheckPermission>
		)}
		{(administrator || superUser || row.permission >= 5) && (
			<CheckPermission minimumPermissionRequired={PermissionEnum.VIEW}>
				<RCCDropDownItem 
					selfEnclose
					onClick={() => {
						if(showCheckboxes)
							actionHideCheckboxes();

						refModalDelete.current?.actionButtonDelete({iqc001Id:row.iqc001Id, iqc001Descricao:row.iqc001Descricao},() => onCallbackDeleteFunction(row.iqc001Id,row.iqc001Nivel));
					}}
				>
					<RCCIcon name="trash" />
					{translate("Excluir")}
				</RCCDropDownItem>
			</CheckPermission>
		)}
	</OptionsColumn>;
	
	const onCallbackUpdateFunction = async (id: number,oldLevel?:IIndicatorsLevel) => {
		try{
			const response = await RCCServicesApi.getRequest({
				method: "Indicators/InfosOfIndicatorForList",
				urlParameters: {
					id,
					...groupersSelect,
					...groupers,
					startDate: filters?.startDate ? formatDate(filters.startDate) : "",
					endDate: filters?.endDate ? formatDate(filters.endDate) : ""
				}
			});
			const indicator = response.data as IListIndicators;

			setList((prevState) => prevState.map((glist) => {
				if(glist.iqc001Id === id)
					return indicator;
				return glist;
			}));

			if(oldLevel && oldLevel !== indicator.iqc001Nivel && onCallbackUpdate)
				onCallbackUpdate(indicator.iqc001Nivel,oldLevel);

		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar indicador, favor atualizar a pagina"), addToast);
		}
	};

	const executeQuery = async () => {
		try{
			const listIndicators = await executeQueryListOfIndicators(groupersSelect, {
				...groupers
			}, filters);
			
			setList(listIndicators);
			setLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar os indicadores"), addToast);
		}
	};

	const loadChildrens = async (id: number, collapse?: boolean) => {
		try{
			setLoadingBackdrop(true);
			const listIndicators = collapse ? [] : await executeQueryListOfIndicatorsChildren(groupersSelect, id, filters);
			setList((prevState) => {
				const updateState = [...prevState];
				const index = updateState.findIndex((x) => x.iqc001Id === id);
				if(index === -1)
				{
					let addedChilds = false;

					const addInTree = (nodes: IListIndicators[]) => {
		
						if(addedChilds)
							return updateState;
		
						if(nodes.some((node) => node.iqc001Id === id)){
							// eslint-disable-next-line no-param-reassign
							nodes[nodes.findIndex((node) => node.iqc001Id === id)].children = listIndicators;
							addedChilds = true;
							return updateState;
						}
											
						nodes.forEach((node) => {
							if(node.children && node.children.length > 0)
								addInTree(node.children);
						});
					
						return updateState;
					};
		
					addInTree(updateState);
					return updateState;
				}
	
				updateState[index].children = listIndicators;
				return updateState;
			});
			setLoadingBackdrop(false);
		}catch(err){
			setLoadingBackdrop(false);
			standardErrorMessages(err, translate("Erro ao carregar os indicadores"), addToast);
		}
	};

	const onCallbackDeleteFunction = (iqc001Id: number, level: IIndicatorsLevel) => {
		setList((prevState) => prevState.filter((x) => x.iqc001Id !== iqc001Id));
		if(onCallbackDelete)
			onCallbackDelete(level);
	};

	useImperativeHandle(ref,() => ({
		executeQuery
	}));

	useEffect(() => {
		if(!list || list.length === 0 || !showCheckboxes)
			return;
		if(defaultChecked){
			list.forEach((el) => addAllSelectedCheckboxesId(el.iqc001Id));
			return;
		}
		list.forEach((el) => addSelectedCheckboxId(el.iqc001Id));
	}, [defaultChecked]);

	if(loading)
		return <Loader />;

	if(!list || list.length === 0)
		return (
			<RCCAlert 
				title={translate("Atenção")}
				description={translate("Não foram encontrados dados")}
			/>
		);		

	return (
		<>
			<GroupIndicators 
				data={list}
				options={(row: IListIndicators) => options(row)}
				showValues={showValues}
				filterList={filterList}
				layoutList={layoutList}
				showHierarchyList={groupersSelect.group === IndicatorsGroupers.INDICATOR_HIERARCHY}
				loadChildrens={loadChildrens}
				openChartIndicators={(iqc001Id, iqc001Descricao, accumulatedValues) => {
					if(showCheckboxes)
						actionHideCheckboxes();

					modalChartIndicatorsValues.current?.actionButtonOpenChartIndicators(iqc001Id, iqc001Descricao, groupersSelect.baseYear, accumulatedValues !== null && accumulatedValues.length > 0, filters?.startDate, filters?.endDate);
				}}
				openValuesOfPeriodIndicator={(id: number, title: string, periodId: number, presentation?: boolean) => {
					if(showCheckboxes)
						actionHideCheckboxes();

					modalValuesOfPeriodIndicators.current?.actionButtonOpenValuesOfPeriodIndicators(id, title, periodId, () => {
						if(onCallbackChangeValuesIndicator)
							onCallbackChangeValuesIndicator();
						executeQuery();
					}, presentation);
				}}
				defaultChecked={defaultChecked}
			/>
			<DeleteModalIndicator ref={refModalDelete}  />
			<ModalIndicatorChart ref={modalChartIndicatorsValues} 
				openValuesOfPeriodIndicator={(id: number, title: string, periodId: number, onCallback?: Function, isPresentation?: boolean) => modalValuesOfPeriodIndicators.current?.actionButtonOpenValuesOfPeriodIndicators(id, title, periodId, onCallback, isPresentation)}
				onCloseCallback={() => {
					if(onCallbackChangeValuesIndicator)
						onCallbackChangeValuesIndicator();
					executeQuery();
				}}
			/>
			<ModalGoalManagement ref={modalGoalManagement}/>
			<IndicatorModal ref={refmodalindicators}/> 
			<ModalIndicatorDetails ref={modalDetailsIndicators} />
			<CheckPermission minimumPermissionRequired={PermissionEnum.VIEW}>
				<ModalIndicatorValuesOfPeriod ref={modalValuesOfPeriodIndicators} submitMarkToUpdate={submitMarkToUpdate} />
			</CheckPermission>
		</>
	);
};

export default forwardRef(ListOfIndicators);
